import { filter } from 'rxjs/operators';

import { computed, DestroyRef, effect, inject, Injectable, linkedSignal, signal, DOCUMENT } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AppTheme } from '@models/types/app-theme';

import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';
import { OktaService } from './okta.service';

@Injectable({
  providedIn: 'root',
})
export class AppModeService {
  private destroyRef = inject(DestroyRef);
  private localStorageService = inject(LocalStorageService);
  private oktaService = inject(OktaService);
  private authService = inject(AuthService);
  private doc = inject(DOCUMENT);
  private isAdmin = signal<boolean>(undefined);
  private loggedInUser$ = this.authService.getUser().pipe(filter((user) => Boolean(user) && this.oktaService.hasValidAccessToken()));
  private userIsExternal$ = this.loggedInUser$.pipe(
    takeUntilDestroyed(this.destroyRef),
    filter((user) => !user.isEngie && this.localStorageService.isAdmin),
  );
  /**
   * to enable theme choices
   */
  public enforceTheme = false;
  private activeTheme = linkedSignal<boolean, AppTheme>({
    source: this.isAdmin,
    computation: (source) => {
      const isAdmin = this.enforceTheme ? undefined : source;
      return typeof isAdmin === 'boolean' ? (isAdmin ? 'dark' : 'light') : this.localStorageService.theme;
    },
  });
  /**
   * For Public usage in the whole app
   */
  public isBusinessEnabled = computed(() => this.isAdmin());
  /**
   * to enable save state on latest vue
   */
  public saveState = true;

  constructor() {
    // this effect is to add class="dark" to HTMl tag
    effect(() => this.applyTheme(this.activeTheme()));
  }

  init() {
    this.setAdmin(this.saveState ? this.localStorageService.isAdmin : false);
    // security, in case we connected with internal user first, the vue state got saved.
    // we need to make sure to erase it when we open a session with external user if exists
    this.userIsExternal$.subscribe(() => this.setAdmin(false));
  }

  setAdmin(value: boolean) {
    this.localStorageService.vue = value ? 'business' : 'client';
    setTimeout(() => this.isAdmin.set(value));
  }

  setTheme(value: AppTheme) {
    this.localStorageService.theme = value;
    this.activeTheme.set(value);
  }

  onVueChange() {
    return this.localStorageService.storageVueChange$;
  }

  private applyTheme(theme: AppTheme) {
    const html = this.doc.documentElement;
    if (theme === 'dark' && !html.classList.contains('dark')) html.classList.add('dark');
    if (theme === 'light' && html.classList.contains('dark')) html.classList.remove('dark');
  }
}
